import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, ArrowRight, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import MedienFeedbackAufnahme, { MedienFeedbackAbspieler } from '@/components/MedienFeedbackAufnahme';

/* Check-in-Auswertung (Coach-Cockpit W1.8/W2.5, 29.10.2026): ein eingereichter Check-in aus dem Ernährungscoaching oder
   P1 Coaching — Antworten mit Δ zum letzten Check-in je Feld, Signale, Bewertung (Auf Kurs · Stabil · Handlungsbedarf),
   Schnellbausteine und Feedbacktext. „Auswerten“ schickt den Feedbacktext in den Coach-Chat (derselbe Verlauf wie die App).
   Wiederverwendbar: `client` ist der Trainer-Client (baseURL /api/coach), Aufrufe gegen /checkins/{id}. Rechnen und
   Signale macht der Server (coach/checkins.py).
   Integration W2 (29.10.2026): Video-/Audio-Feedback zur Auswertung (W2.6) — `MedienFeedbackAufnahme` mit
   `bezug="checkin"` über `/checkins/{id}/medien` (Sichtregel der Inbox; das Feedback geht wie jedes Medium in den
   Coach-Chat, die Karte zeigt „angesehen“). */

/* Status-Farben wie im Mockup (Auf Kurs #13301f/#7fd1a0, Beobachten #33290f/#f3c46b, Kritisch #3a1c18/#ff9a8a); hell über Tokens. */
export const BEWERTUNG_STIL = {
  auf_kurs: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:border-[#1f4a31] dark:bg-[#13301f] dark:text-[#7fd1a0]',
  stabil: 'border-sky-500/50 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  handlungsbedarf: 'border-red-500/50 bg-red-500/10 text-red-700 dark:border-[#5a2a24] dark:bg-[#3a1c18] dark:text-[#ff9a8a]',
};
export const KARTE = 'rounded-[14px] border border-border bg-card px-5 py-[18px]';
const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const zeitDe = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
const zahlDe = (w) => (typeof w === 'number' ? String(Math.round(w * 10) / 10).replace('.', ',') : w);

export function SignalChip({ s }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${s.art === 'warnung'
      ? 'border-amber-500/60 bg-amber-500/10 text-amber-800 dark:text-amber-200' : 'border-emerald-500/50 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200'}`}>{s.text}</span>
  );
}

function wertText(f) {
  const w = f.wert;
  if (w === null || w === undefined || w === '') return '—';
  if (f.typ === 'foto') return 'Foto';
  if (typeof w === 'boolean') return w ? 'Ja' : 'Nein';
  if (Array.isArray(w)) return w.join(', ');
  if (typeof w === 'object') return Object.keys(w).length ? `${Object.keys(w).length} Angaben` : '—';
  if (f.typ === 'skala' || f.typ === 'bewertung') return `${zahlDe(w)} / ${f.max || (f.typ === 'bewertung' ? 5 : 10)}`;
  return `${zahlDe(w)}${f.einheit ? ` ${f.einheit}` : ''}`;
}

const UMFANG = { brust: 'Brust', taille: 'Taille', huefte: 'Hüfte', oberschenkel: 'Oberschenkel', arm: 'Arm', waden: 'Waden', hals: 'Hals', schulter: 'Schulter' };
const umfangName = (k) => UMFANG[k] || (k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' '));
const ROT = 'text-red-600 dark:text-[#ff9a8a]';
const GRUEN = 'text-emerald-700 dark:text-[#7fd1a0]';

function Delta({ f }) {
  if (typeof f.delta !== 'number' || f.delta === 0) return <span className="text-xs font-extrabold text-muted-foreground">{typeof f.vorher === 'number' ? '±0' : ''}</span>;
  const gut = f.delta * (f.better || 1) > 0;
  return <span className={`text-xs font-extrabold ${gut ? GRUEN : ROT}`}>{f.delta > 0 ? '+' : '−'}{zahlDe(Math.abs(f.delta))}</span>;
}

function Signal({ f }) {
  return (
    <div className="rounded-[12px] border border-border bg-background px-3.5 py-3" data-testid={`signal-${f.id}`}>
      <div className="line-clamp-2 text-[13px] text-muted-foreground">{f.text}</div>
      <div className="coach-zahl text-[22px] font-extrabold leading-tight">{wertText(f)}</div>
      <Delta f={f} />
    </div>
  );
}

/* Körpermaße: Felder vom Typ umfaenge (Objekt Stelle → cm); „zuletzt“ nur, wenn der Server einen früheren Wert liefert. */
function Koerpermasse({ felder }) {
  const zeilen = [];
  felder.filter((f) => f.typ === 'umfaenge' && f.wert && typeof f.wert === 'object').forEach((f) => {
    const vor = f.vorher && typeof f.vorher === 'object' ? f.vorher : {};
    Object.entries(f.wert).forEach(([k, v]) => {
      if (typeof v !== 'number') return;
      zeilen.push({ k: `${f.id}-${k}`, name: umfangName(k), v, d: typeof vor[k] === 'number' ? Math.round((v - vor[k]) * 10) / 10 : null });
    });
  });
  if (!zeilen.length) return null;
  return (
    <section className={KARTE} data-testid="checkin-masse">
      <h2 className="mb-3 text-base font-bold">Körpermaße</h2>
      <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2.5 pb-2 text-[13px] font-bold text-muted-foreground"><span>Stelle</span><span>aktuell</span><span>zuletzt</span></div>
      {zeilen.map((z) => (
        <div key={z.k} className="grid grid-cols-[1.4fr_1fr_1fr] gap-2.5 border-t border-border py-2 text-sm">
          <span>{z.name}</span><span>{zahlDe(z.v)} cm</span>
          <span className={z.d === null || z.d === 0 ? 'text-xs font-extrabold text-muted-foreground' : `text-xs font-extrabold ${z.d < 0 ? GRUEN : ROT}`}>{z.d === null ? '—' : z.d === 0 ? '±0' : `${z.d > 0 ? '+' : '−'}${zahlDe(Math.abs(z.d))}`}</span>
        </div>
      ))}
    </section>
  );
}

/* Video-/Audio-Feedback zu diesem Check-in: Aufnahme und die bisher gesendeten mit „angesehen am …“. */
function MedienZurAuswertung({ client, id, leadId, disabled }) {
  const [liste, setListe] = useState(null);
  const laden = useCallback(() => {
    client.get(`/checkins/${id}/medien`).then((r) => setListe(r.data?.eintraege || [])).catch(() => setListe([]));
  }, [client, id]);
  useEffect(() => { laden(); }, [laden]);
  const medienClient = useMemo(() => ({ hochladen: (fd, cfg) => client.post(`/checkins/${id}/medien`, fd, cfg) }), [client, id]);
  const datei = useCallback((fid) => client.get(`/checkins/${id}/medien/${fid}/datei`, { responseType: 'blob' }), [client, id]);
  return (
    <div className="space-y-3 rounded-[12px] border border-border bg-background p-3" data-testid="checkin-medien">
      <p className="font-extrabold">Persönliches Feedback</p>
      <p className="text-xs text-muted-foreground">Video oder Audio bis 3 Minuten, geht als Nachricht in den Coach-Chat. Du siehst, wann es angesehen wurde.</p>
      <MedienFeedbackAufnahme leadId={leadId} bezug="checkin" bezugId={id} client={medienClient} onGesendet={laden} disabled={disabled} />
      {liste?.length > 0 && (
        <ul className="space-y-2 border-t border-border pt-3">
          {liste.map((m) => <li key={m.id}><MedienFeedbackAbspieler medien={{ ...m, geloescht: m.status === 'geloescht' }} datei={datei} /></li>)}
        </ul>
      )}
    </div>
  );
}

/* Mitte + rechte Spalte der Check-in-Ansicht (Mockup „Check-ins“). Gibt ein Fragment aus zwei Spalten zurück — der
   Aufrufer legt sie in eine umbrechende Zeile (links die Inbox-Liste). `rechts(d)` = Rhythmus-Karte (nur /coach-Inbox),
   `onEinschieben(mitgliedId)` = Kopf-Knopf „Check-in einschieben“. */
export default function CheckinAuswertung({ client, id, onWeiter, onGeaendert, rechts, onEinschieben }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [bewertung, setBewertung] = useState('');
  const [bausteine, setBausteine] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState(false);
  const laden = useCallback(() => {
    setD(null); setErr('');
    client.get(`/checkins/${id}`).then((r) => {
      setD(r.data); setBewertung(r.data.auswertung?.bewertung || ''); setBausteine([]); setText('');
    }).catch((e) => setErr(fehlertext(e, 'Check-in nicht geladen')));
  }, [client, id]);
  useEffect(() => { laden(); }, [laden]);

  if (err) return <div className={`${KARTE} min-w-0 flex-[5_1_560px] py-8 text-center text-sm text-destructive`} role="alert">{err}</div>;
  if (!d) return <div className="min-w-0 flex-[5_1_560px]"><Skeleton className="h-96 w-full rounded-[14px]" /></div>;

  const ausgewertet = d.stand === 'ausgewertet';
  const archiv = d.stand === 'archiv';
  const offen = !ausgewertet && !archiv;
  const vorname = (d.name || 'Mitglied').split(/\s+/)[0];
  const vorschau = [...bausteine.map((b) => d.bausteine.find((x) => x.id === b)?.text), text.trim()].filter(Boolean).join('\n\n');
  const umschalten = (b) => setBausteine((l) => (l.includes(b) ? l.filter((x) => x !== b) : [...l, b]));
  const signale = d.felder.filter((f) => typeof f.wert === 'number' && f.typ !== 'umfaenge').slice(0, 8);
  const warnungen = d.signale.filter((s) => s.art === 'warnung');
  const gute = d.signale.filter((s) => s.art !== 'warnung');
  const fotos = d.felder.filter((f) => f.typ === 'fortschrittsfotos' || f.typ === 'foto').filter((f) => f.wert && (!Array.isArray(f.wert) || f.wert.length));

  const auswerten = async () => {
    if (!bewertung) { toast.error('Bitte eine Bewertung wählen'); return; }
    setBusy(true);
    try {
      const r = await client.post(`/checkins/${id}/auswerten`, { bewertung, bausteine, text: text.trim() || null });
      toast.success(vorschau ? 'Ausgewertet – das Feedback steht im Chat' : 'Ausgewertet');
      setD(r.data); onGeaendert?.();
    } catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };
  const archivieren = async (zurueck = false) => {
    setBusy(true);
    try {
      const r = await client.post(`/checkins/${id}/archivieren`, { zurueck });
      toast.success(zurueck ? 'Zurückgeholt' : 'Archiviert'); onGeaendert?.();
      if (!zurueck && r.data.naechster_id && onWeiter) onWeiter(r.data.naechster_id); else laden();
    } catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };
  const knopf = 'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-[10px] border border-border bg-muted px-4 text-sm font-bold text-foreground transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

  return (
    <>
      <div className="flex min-w-0 flex-[3_1_480px] flex-col gap-[18px]" data-testid="checkin-auswertung">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-extrabold">Check-in von {d.name || 'Mitglied'}</h2>
            <p className="text-[13px] text-muted-foreground">
              {zeitDe(d.eingereicht_am)}{d.woche ? ` · ${d.woche}` : ''} · {d.titel} · {d.system_name}{d.eingeschoben ? ' · eingeschoben' : ''}
              {d.bewertung && <span className={`ml-2 rounded-full border px-2 py-0.5 text-[11px] font-bold ${BEWERTUNG_STIL[d.bewertung]}`}>{d.bewertungen[d.bewertung]}</span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {rechts && <button type="button" className={knopf} onClick={() => setPanel((p) => !p)} aria-expanded={panel} data-testid="kopf-rhythmus">Rhythmus festlegen</button>}
            {rechts && onEinschieben && <button type="button" className={knopf} onClick={() => onEinschieben(d.mitglied_id)} data-testid="kopf-einschieben">Check-in einschieben</button>}
            {rechts && <button type="button" className={knopf} onClick={() => setPanel(true)} data-testid="kopf-startwerte">Startwerte nachtragen</button>}
            {d.naechster_id && onWeiter && (
              <button type="button" className={knopf} onClick={() => onWeiter(d.naechster_id)} data-testid="checkin-wechseln">Check-in wechseln <ArrowRight className="h-4 w-4" aria-hidden="true" /></button>
            )}
          </div>
        </header>
        {panel && rechts && <div data-testid="kopf-panel">{rechts(d)}</div>}

        <section className={KARTE}>
          <h2 className="mb-3 flex items-center gap-2 text-base font-bold">Die wichtigsten Signale<span className="ml-auto text-[13px] font-semibold text-muted-foreground">Vergleich zum letzten Check-in</span></h2>
          {signale.length > 0
            ? <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>{signale.map((f) => <Signal key={f.id} f={f} />)}</div>
            : <p className="text-sm text-muted-foreground">Keine Zahlenwerte in diesem Check-in.</p>}
          {typeof d.gewicht?.aktuell === 'number' && !signale.some((f) => f.wert === d.gewicht.aktuell) && (
            <p className="mt-3 text-xs text-muted-foreground">Gewicht {zahlDe(d.gewicht.aktuell)} kg{typeof d.gewicht.vorher === 'number' ? ` (vorher ${zahlDe(d.gewicht.vorher)} kg)` : ''}{typeof d.gewicht.ziel === 'number' ? ` · Ziel ${zahlDe(d.gewicht.ziel)} kg` : ''}</p>
          )}
          {warnungen.length > 0 && (
            <div className="mt-3 rounded-[10px] bg-amber-500/10 px-3 py-2.5 text-[13px] text-amber-800 dark:bg-[#33290f] dark:text-[#f3c46b]" role="status" aria-label="Auffälligkeiten">
              Auffällig: {warnungen.map((s) => s.text).join(' · ')}
            </div>
          )}
          {gute.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Signale">{gute.map((s) => <SignalChip key={s.text} s={s} />)}</div>}
          {d.zitat && <blockquote className="mt-3 rounded-[10px] bg-background px-3.5 py-3 text-[13px] leading-relaxed">„{d.zitat}“</blockquote>}
        </section>

        <Koerpermasse felder={d.felder} />

        {fotos.length > 0 && (
          <section className={KARTE} data-testid="checkin-fotos">
            <h2 className="mb-2 text-base font-bold">Fortschrittsfotos</h2>
            <p className="text-sm text-muted-foreground">{fotos.map((f) => `${f.text}: ${Array.isArray(f.wert) ? `${f.wert.length} Foto${f.wert.length === 1 ? '' : 's'} hochgeladen` : 'hochgeladen'}`).join(' · ')}. Die Bilder öffnest du in der Kundenakte unter Entwicklung.</p>
          </section>
        )}

        <details className={KARTE} data-testid="checkin-antworten">
          <summary className="min-h-11 cursor-pointer text-base font-bold leading-[44px]">Alle Antworten</summary>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 pr-3 font-medium">Frage</th><th className="py-1 pr-3 font-medium">Antwort</th><th className="py-1 font-medium">Δ zum letzten</th></tr></thead>
              <tbody>
                {d.felder.map((f) => (
                  <tr key={f.id} className="border-t border-border align-top">
                    <td className="py-2 pr-3 text-muted-foreground">{f.text}</td>
                    <td className="whitespace-pre-wrap break-words py-2 pr-3 font-medium">{wertText(f)}</td>
                    <td className="py-2"><Delta f={f} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </div>

      <section className={`${KARTE} flex min-w-0 flex-[2_1_300px] flex-col gap-3.5 border-marke/40`} aria-label="Deine Auswertung">
        <h2 className="text-base font-bold">Deine Auswertung</h2>
        {(ausgewertet || archiv) && (d.auswertung || d.coach_antwort) ? (
          <div className="space-y-2 text-sm">
            {d.auswertung && <p className="text-xs text-muted-foreground">{d.auswertung.von_name} · {zeitDe(d.auswertung.am)}</p>}
            {(d.auswertung?.text || d.coach_antwort) ? <p className="whitespace-pre-wrap rounded-[10px] bg-background p-3">{d.auswertung?.text || d.coach_antwort}</p>
              : <p className="text-muted-foreground">Ohne Feedbacktext ausgewertet.</p>}
          </div>
        ) : null}
        {archiv && <p className="text-sm text-muted-foreground">Archiviert.</p>}
        {offen && (
          <>
            <div>
              <div className="mb-1.5 text-[13px] text-muted-foreground">Bewertung</div>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Bewertung">
                {Object.entries(d.bewertungen).map(([w, l]) => (
                  <button key={w} type="button" onClick={() => setBewertung(w)} aria-pressed={bewertung === w} data-testid={`bewertung-${w}`}
                    className={`min-h-12 flex-1 basis-24 rounded-[10px] border px-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${bewertung === w ? 'coach-hinweis border-2 border-marke' : 'border-border bg-background hover:bg-muted'}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-[13px] text-muted-foreground">Schnellbausteine</div>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Schnellbausteine">
                {d.bausteine.map((b) => (
                  <button key={b.id} type="button" onClick={() => umschalten(b.id)} aria-pressed={bausteine.includes(b.id)} title={b.text} data-testid={`baustein-${b.id}`}
                    className={`min-h-11 rounded-full border px-[11px] py-[5px] text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${bausteine.includes(b.id) ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted text-foreground hover:bg-muted/70'}`}>+ {b.titel}</button>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="checkin-feedback" className="text-[13px] text-muted-foreground">Feedback an {vorname}</label>
              <Textarea id="checkin-feedback" value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={2000} className="mt-1.5 bg-background" placeholder="Dein Feedback (optional) – geht zusammen mit den Bausteinen in den Chat" data-testid="feedback-text" />
            </div>
            {vorschau && <p className="whitespace-pre-wrap rounded-[10px] border border-dashed border-border p-3 text-xs text-muted-foreground" aria-label="Vorschau der Nachricht">{vorschau}</p>}
          </>
        )}
        <MedienZurAuswertung client={client} id={id} leadId={d.mitglied_id} disabled={archiv} />
        <div className="flex flex-wrap gap-2">
          {offen && (
            <Button className="min-h-11 flex-1 gap-1.5 rounded-[10px] font-bold" onClick={auswerten} disabled={busy || !bewertung} data-testid="checkin-auswerten">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Abschließen &amp; senden
            </Button>
          )}
          {archiv
            ? <button type="button" className={knopf} onClick={() => archivieren(true)} disabled={busy} data-testid="checkin-zurueckholen"><ArchiveRestore className="h-4 w-4" aria-hidden="true" /> Zurückholen</button>
            : <button type="button" className={knopf} onClick={() => archivieren(false)} disabled={busy} data-testid="checkin-archivieren"><Archive className="h-4 w-4" aria-hidden="true" /> Archivieren</button>}
        </div>
        {offen && <p className="text-[13px] text-muted-foreground">Beim Abschluss geht das Feedback in den Coach-Chat; {vorname} bekommt eine Push ohne Inhalt.</p>}
      </section>
    </>
  );
}
